/**
 * @fileoverview Firm Contact Detail Sheet
 *
 * Purpose: Detail sheet for an individual firm contact. Identity fields
 * (name, category, contacts, addresses, archive) are gated to
 * owners/admins via `canEdit`; the Notes tab stays editable for every
 * org member. Both rules are enforced server-side on the mutations.
 *
 * Tabs:
 * - Overview: identity fields + jobs-using deep-links (autosave)
 * - Notes: firm-level notes (autosave)
 * - History: change log via shared `ChangeHistory`
 *
 * For org_member rows the identity fields fall back to the linked user
 * profile (read-only); admins can still edit category, notes, and
 * non-identity fields.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import { convexQueryOrIdle } from "@/lib/convex-query-idle";
import type { Id } from "@cvx/_generated/dataModel";
import type { FunctionReturnType } from "convex/server";
import { cn } from "@/utils/misc";
import {
  Archive,
  ArchiveRestore,
  ArrowUpRight,
  Loader2,
  Plus,
  Tag,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { roleIconComponent } from "@/components/settings/tasks/role-icons";
import { CustomFieldBadge } from "@/components/pre-appointment/custom-field-badge";
import {
  formatCustomFieldValue,
  isSummable,
} from "@/components/pre-appointment/custom-fields";
import { toast } from "sonner";
import { Badge } from "@/ui/primitives/badge";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import { Label } from "@/ui/primitives/label";
import { ScrollArea } from "@/ui/primitives/scroll-area";
import { Separator } from "@/ui/primitives/separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/ui/composed/overlays/sheet";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/ui/composed/sections/tabs";
import { AiDisclaimer } from "@/ui/composed/ai-disclaimer";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import { useAutosave } from "@/utils/forms/use-autosave";
import { AutosaveStatus } from "@/utils/forms/autosave-status";
import {
  ChangeHistory,
  type ResolvedHistoryUser,
} from "@/components/jobs/phases/gather/summarization/shared-panels/ChangeHistory";
import {
  CONTACT_FIELD_FORMATTERS,
  CONTACT_FIELD_LABELS,
} from "@/components/jobs/phases/contacts/historyFormatters";
import {
  CONTACT_TYPE_META,
  CONTACT_TYPE_ORDER,
  FIRM_CATEGORY_META,
} from "./types";
import type { ContactAddress, ContactType, FirmContactCategory } from "./types";
import { AddressesEditor } from "@/components/jobs/phases/contacts/AddressesEditor";
import { createEmptyAddress } from "@/components/jobs/phases/contacts/types";
import { FirmContactNotesFeed } from "./FirmContactNotesFeed";
import { APPOINTMENT_TYPE_TAGS } from "@/lib/precedent-templates";
import { JOB_STATUS_LABELS, JOB_STATUS_VARIANTS } from "@/lib/job-statuses";

interface FirmContactDetailPanelProps {
  firmContactId: Id<"firmContacts"> | null;
  open: boolean;
  /** Owners/admins can edit; members see a read-only sheet. */
  canEdit: boolean;
  onClose: () => void;
  /**
   * MOR-995 — position in the side-by-side stack. `0` is the primary sheet
   * (flush right, dimmed backdrop). `1`, `2`, … are linked-contact panels
   * that sit to the left of it, non-modal so the primary stays interactive
   * (mirrors `SlideOverPanel` in the summarisation panels).
   */
  stackIndex?: number;
}

/** Width of one contact sheet (`sm:max-w-2xl`); secondaries offset by this. */
const SHEET_WIDTH_REM = 42;

export function FirmContactDetailPanel({
  firmContactId,
  open,
  canEdit,
  onClose,
  stackIndex = 0,
}: FirmContactDetailPanelProps) {
  const isSecondary = stackIndex > 0;
  // MOR-995 — a linked contact (a person's firm, a firm's people) opens in
  // a second sheet layered over this one, like the mail contact pop-out,
  // so the original card stays put behind it. Reset when this sheet
  // changes contact or closes.
  const [linkedId, setLinkedId] = useState<Id<"firmContacts"> | null>(null);
  useEffect(() => {
    setLinkedId(null);
  }, [firmContactId, open]);

  return (
    <Sheet
      open={open}
      modal={!isSecondary}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <SheetContent
        side="right"
        className={cn(
          "flex w-full flex-col gap-0 p-0 sm:max-w-2xl",
          isSecondary && "shadow-xl",
        )}
        style={
          isSecondary
            ? { right: `${stackIndex * SHEET_WIDTH_REM}rem` }
            : undefined
        }
        // A secondary panel must not close when the user clicks back into
        // the primary card (or another panel in the stack).
        onInteractOutside={isSecondary ? (e) => e.preventDefault() : undefined}
      >
        {firmContactId ? (
          <FirmContactDetailBody
            key={firmContactId}
            firmContactId={firmContactId}
            canEdit={canEdit}
            onOpenContact={(id) => setLinkedId(id)}
          />
        ) : null}
        {/* Nested pop-out for the linked contact. */}
        <FirmContactDetailPanel
          firmContactId={linkedId}
          open={linkedId !== null}
          canEdit={canEdit}
          onClose={() => setLinkedId(null)}
          stackIndex={stackIndex + 1}
        />
      </SheetContent>
    </Sheet>
  );
}

const CREATABLE_CATEGORIES: ReadonlyArray<FirmContactCategory> = [
  "bank",
  "regulator",
  "professional",
  "org_member",
  "referrer",
  "other",
];

type EditableFields = {
  displayName: string;
  email: string;
  phone: string;
  website: string;
  abn: string;
  acn: string;
};

function emptyEditable(): EditableFields {
  return {
    displayName: "",
    email: "",
    phone: "",
    website: "",
    abn: "",
    acn: "",
  };
}

function FirmContactDetailBody({
  firmContactId,
  canEdit,
  onOpenContact,
}: {
  firmContactId: Id<"firmContacts">;
  canEdit: boolean;
  /** Swap the sheet to another contact's card (parent firm / nested person). */
  onOpenContact: (id: Id<"firmContacts">) => void;
}) {
  const { data: firm, isLoading } = useQuery({
    ...convexQuery(api.contacts.queries.getFirmContact, { firmContactId }),
  });

  const { data: jobsUsing } = useQuery({
    ...convexQuery(api.contacts.queries.listJobsUsingFirmContact, {
      firmContactId,
    }),
  });

  // Current user drives author-only edit/delete on the notes feed.
  const { data: currentUser } = useQuery({
    ...convexQuery(api.users.queries.getCurrentUser, {}),
  });
  const currentUserId = currentUser?._id;

  // Custom field definitions for this contact's category (all contacts in
  // a category share the same fields). Loaded once `firm` resolves.
  const { data: fieldDefinitions } = useQuery(
    convexQueryOrIdle(
      api.contacts.fieldDefinitions.listFirmContactFieldDefinitions,
      firm ? { orgId: firm.orgId, category: firm.category } : undefined,
    ),
  );

  // Org-defined custom categories (MOR-914) for the category picker.
  const { data: customCategoriesData } = useQuery(
    convexQueryOrIdle(
      api.contacts.customCategories.listFirmContactCategories,
      firm ? { orgId: firm.orgId } : undefined,
    ),
  );
  const customCategories = useMemo(
    () => customCategoriesData ?? [],
    [customCategoriesData],
  );

  const updateContact = useConvexMutation(
    api.contacts.mutations.updateFirmContact,
  );
  const archive = useConvexMutation(api.contacts.mutations.archiveFirmContact);
  const unarchive = useConvexMutation(
    api.contacts.mutations.unarchiveFirmContact,
  );

  // Resolve practitioner identities referenced by the change-history feed
  // (full name + avatar) in a single batched Convex subscription.
  const historyUserIds = useMemo(() => {
    const ids = new Set<Id<"users">>();
    for (const entry of firm?.changeHistory ?? []) {
      if (entry.userId) ids.add(entry.userId);
    }
    return Array.from(ids);
  }, [firm?.changeHistory]);
  const { data: historyUsers } = useQuery({
    ...convexQuery(api.users.queries.listUsersByIds, {
      ids: historyUserIds,
    }),
    enabled: historyUserIds.length > 0,
  });
  const historyUsersById = useMemo(() => {
    const map = new Map<Id<"users">, ResolvedHistoryUser>();
    for (const u of historyUsers ?? []) {
      map.set(u._id, u);
    }
    return map;
  }, [historyUsers]);

  const [draft, setDraft] = useState<EditableFields>(emptyEditable);
  const draftRef = useRef<EditableFields>(emptyEditable());
  draftRef.current = draft;

  const [category, setCategory] = useState<FirmContactCategory>("other");
  /** Selected custom category id (MOR-914), or null for a built-in category. */
  const [customCategoryId, setCustomCategoryId] = useState<string | null>(null);
  const [contactType, setContactType] = useState<ContactType>("organisation");
  /** MOR-995 — parent firm this contact nests under (person → organisation). */
  const [parentFirmContactId, setParentFirmContactId] = useState<string | null>(
    null,
  );

  // MOR-995 — the org's directory, for the parent-firm picker (organisation
  // contacts that aren't themselves nested) and the nested-people list on a
  // firm's panel.
  const { data: orgContacts } = useQuery(
    convexQueryOrIdle(
      api.contacts.queries.listFirmContacts,
      firm ? { orgId: firm.orgId } : undefined,
    ),
  );
  const parentOptions = useMemo(
    () =>
      (orgContacts ?? []).filter(
        (c) =>
          c._id !== firmContactId &&
          c.contactType === "organisation" &&
          !c.parentFirmContactId &&
          c.status === "active",
      ),
    [orgContacts, firmContactId],
  );
  const nestedPeople = useMemo(
    () =>
      (orgContacts ?? []).filter(
        (c) => c.parentFirmContactId === firmContactId,
      ),
    [orgContacts, firmContactId],
  );

  const [addressesDraft, setAddressesDraft] = useState<Array<ContactAddress>>(
    [],
  );
  const addressesDraftRef = useRef<Array<ContactAddress>>([]);
  addressesDraftRef.current = addressesDraft;

  // Custom field values, keyed by definition `key`. Stored raw; formatted
  // at render time when pulled into precedents.
  const [customFieldsDraft, setCustomFieldsDraft] = useState<
    Record<string, string>
  >({});
  const customFieldsDraftRef = useRef<Record<string, string>>({});
  customFieldsDraftRef.current = customFieldsDraft;

  const hydratedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!firm) return;
    if (hydratedForRef.current === firm._id) return;
    hydratedForRef.current = firm._id;
    setDraft({
      displayName: firm.displayName,
      email: firm.email ?? "",
      phone: firm.phone ?? "",
      website: firm.website ?? "",
      abn: firm.abn ?? "",
      acn: firm.acn ?? "",
    });
    setCategory(firm.category);
    setCustomCategoryId(firm.customCategoryId ?? null);
    setContactType(firm.contactType);
    setParentFirmContactId(firm.parentFirmContactId ?? null);
    setAddressesDraft(firm.addresses);
    setCustomFieldsDraft(firm.customFields ?? {});
  }, [firm]);

  const fieldsAutosave = useAutosave<
    EditableFields & {
      category: FirmContactCategory;
      customCategoryId: string | null;
      contactType: ContactType;
      parentFirmContactId: string | null;
    }
  >({
    getValue: () => ({
      ...draftRef.current,
      category,
      customCategoryId,
      contactType,
      parentFirmContactId,
    }),
    save: async (value) => {
      await updateContact({
        firmContactId,
        patch: {
          displayName: value.displayName,
          email: value.email,
          phone: value.phone,
          website: value.website,
          abn: value.abn,
          acn: value.acn,
          category: value.category,
          customCategoryId:
            value.customCategoryId as Id<"firmContactCategories"> | null,
          contactType: value.contactType,
          // Only people nest under a firm; switching type away from person
          // detaches the contact.
          parentFirmContactId:
            value.contactType === "person"
              ? (value.parentFirmContactId as Id<"firmContacts"> | null)
              : null,
        },
      });
    },
  });

  const customFieldsAutosave = useAutosave<Record<string, string>>({
    getValue: () => customFieldsDraftRef.current,
    save: async (value) => {
      await updateContact({ firmContactId, patch: { customFields: value } });
    },
  });

  const addressesAutosave = useAutosave<Array<ContactAddress>>({
    getValue: () => addressesDraftRef.current,
    save: async (value) => {
      await updateContact({
        firmContactId,
        patch: {
          addresses: value.map((a) => ({
            line1: a.line1,
            line2: a.line2,
            suburb: a.suburb,
            state: a.state,
            postcode: a.postcode,
            country: a.country,
            sourceLabel: a.sourceLabel,
          })),
        },
      });
    },
  });

  useEffect(() => {
    if (!firm) return;
    fieldsAutosave.resetBaseline({
      displayName: firm.displayName,
      email: firm.email ?? "",
      phone: firm.phone ?? "",
      website: firm.website ?? "",
      abn: firm.abn ?? "",
      acn: firm.acn ?? "",
      category: firm.category,
      customCategoryId: firm.customCategoryId ?? null,
      contactType: firm.contactType,
      parentFirmContactId: firm.parentFirmContactId ?? null,
    });
    addressesAutosave.resetBaseline(firm.addresses);
    customFieldsAutosave.resetBaseline(firm.customFields ?? {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firm?._id]);

  if (isLoading || !firm) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Loading firm contact…
      </div>
    );
  }

  const isOrgMember = firm.category === "org_member";
  const isArchived = firm.status === "archived";
  const TypeIcon = CONTACT_TYPE_META[firm.contactType].icon;
  const linkedDisplayName = firm.linkedUser
    ? [firm.linkedUser.firstName, firm.linkedUser.lastName]
        .filter((s): s is string => Boolean(s))
        .join(" ") || firm.linkedUser.email
    : null;

  // org_member identity fields read through to the linked user profile.
  // We disable editing on those fields and surface a hint. Members
  // without edit access (`!canEdit`) get every field disabled.
  const identityDisabled = isOrgMember || !canEdit;

  const handleArchive = () => {
    archive({ firmContactId })
      .then(() => toast.success("Firm contact archived."))
      .catch((e: Error) =>
        toast.error(e.message || "Could not archive contact."),
      );
  };

  const handleUnarchive = () => {
    unarchive({ firmContactId })
      .then(() => toast.success("Firm contact restored."))
      .catch((e: Error) =>
        toast.error(e.message || "Could not restore contact."),
      );
  };

  return (
    <>
      <SheetHeader className="border-b border-border px-6 pb-4 pt-6">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-muted p-2">
            <TypeIcon className="h-5 w-5 text-foreground" />
          </div>
          <div className="min-w-0 flex-1">
            <SheetTitle className="text-lg leading-tight">
              {linkedDisplayName ?? firm.displayName}
            </SheetTitle>
            <SheetDescription className="sr-only">
              Firm contact details
            </SheetDescription>
          </div>
        </div>
      </SheetHeader>

      {isArchived && (
        <div className="flex items-center justify-between gap-2 border-b border-border bg-muted/30 px-6 py-2 text-xs text-muted-foreground">
          <span>This contact is archived. New jobs will not auto-link.</span>
          {canEdit && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleUnarchive}
              className="gap-2"
            >
              <ArchiveRestore className="h-3.5 w-3.5" />
              Restore
            </Button>
          )}
        </div>
      )}

      <Tabs defaultValue="overview" className="flex min-h-0 flex-1 flex-col">
        <div className="px-6 pt-3">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="overview">Details</TabsTrigger>
            <TabsTrigger value="notes">Notes</TabsTrigger>
            <TabsTrigger value="history">
              History
              <span className="ml-1.5 rounded-sm bg-muted px-1 text-[10px] tabular-nums text-muted-foreground">
                {firm.changeHistory.length}
              </span>
            </TabsTrigger>
          </TabsList>
        </div>

        <ScrollArea className="min-h-0 flex-1">
          <TabsContent value="overview" className="mt-0 px-6 py-5">
            <div className="flex flex-col gap-5">
              <section className="flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-sans text-xs font-semibold uppercase tracking-wider text-foreground">
                    Details
                  </h3>
                  <AutosaveStatus
                    status={fieldsAutosave.status}
                    lastSavedAt={fieldsAutosave.lastSavedAt}
                    onRetry={fieldsAutosave.retry}
                  />
                </div>
                {isOrgMember && (
                  <p className="rounded-md bg-muted/30 p-2 text-[11px] text-muted-foreground">
                    This row is linked to a user account. Name and email come
                    from their profile and update automatically.
                  </p>
                )}
                <FieldRow label="Name">
                  <Input
                    value={draft.displayName}
                    disabled={identityDisabled}
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, displayName: e.target.value }))
                    }
                    onBlur={() => fieldsAutosave.flush()}
                  />
                </FieldRow>
                <div className="grid grid-cols-2 gap-3">
                  <FieldRow label="Category">
                    <Select
                      value={
                        customCategoryId
                          ? `custom:${customCategoryId}`
                          : category
                      }
                      disabled={!canEdit}
                      onValueChange={(v) => {
                        if (v.startsWith("custom:")) {
                          setCustomCategoryId(v.slice("custom:".length));
                        } else {
                          setCustomCategoryId(null);
                          setCategory(v as FirmContactCategory);
                        }
                        // Flush soon after selection so the change isn't lost.
                        setTimeout(() => fieldsAutosave.flush(), 0);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CREATABLE_CATEGORIES.map((c) => {
                          const m = FIRM_CATEGORY_META[c];
                          return (
                            <SelectItem key={c} value={c}>
                              <span className="inline-flex items-center gap-2">
                                <m.icon className="h-3.5 w-3.5" />
                                {m.label}
                              </span>
                            </SelectItem>
                          );
                        })}
                        {customCategories.map((c) => {
                          const Icon = roleIconComponent(c.icon) ?? Tag;
                          return (
                            <SelectItem key={c._id} value={`custom:${c._id}`}>
                              <span className="inline-flex items-center gap-2">
                                <Icon className="h-3.5 w-3.5" />
                                {c.name}
                              </span>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </FieldRow>
                  <FieldRow label="Type">
                    <Select
                      value={contactType}
                      disabled={!canEdit}
                      onValueChange={(v) => {
                        setContactType(v as ContactType);
                        setTimeout(() => fieldsAutosave.flush(), 0);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CONTACT_TYPE_ORDER.map((t) => {
                          const m = CONTACT_TYPE_META[t];
                          return (
                            <SelectItem key={t} value={t}>
                              <span className="inline-flex items-center gap-2">
                                <m.icon className="h-3.5 w-3.5" />
                                {m.label}
                              </span>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </FieldRow>
                </div>
                {/* MOR-995 — nest a person under the firm they work for, so
                    referrals record both the individual and the firm and
                    the firm's referral stats roll the person's up. */}
                {contactType === "person" && (
                  <FieldRow label="Firm">
                    <div className="flex items-center gap-2">
                      <Select
                        value={parentFirmContactId ?? "__none__"}
                        disabled={!canEdit}
                        onValueChange={(v) => {
                          setParentFirmContactId(v === "__none__" ? null : v);
                          setTimeout(() => fieldsAutosave.flush(), 0);
                        }}
                      >
                        <SelectTrigger className="flex-1">
                          <SelectValue placeholder="No firm" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">No firm</SelectItem>
                          {parentOptions.map((c) => (
                            <SelectItem key={c._id} value={String(c._id)}>
                              {c.displayName}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {parentFirmContactId && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="shrink-0 gap-1"
                          title="Open the firm's contact card"
                          onClick={() =>
                            onOpenContact(
                              parentFirmContactId as Id<"firmContacts">,
                            )
                          }
                        >
                          <ArrowUpRight className="h-3.5 w-3.5" />
                          Open card
                        </Button>
                      )}
                    </div>
                  </FieldRow>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <FieldRow label="Email">
                    <Input
                      type="email"
                      value={draft.email}
                      disabled={identityDisabled}
                      onChange={(e) =>
                        setDraft((d) => ({ ...d, email: e.target.value }))
                      }
                      onBlur={() => fieldsAutosave.flush()}
                    />
                  </FieldRow>
                  <FieldRow label="Phone">
                    <Input
                      value={draft.phone}
                      disabled={!canEdit}
                      onChange={(e) =>
                        setDraft((d) => ({ ...d, phone: e.target.value }))
                      }
                      onBlur={() => fieldsAutosave.flush()}
                    />
                  </FieldRow>
                </div>
                <FieldRow label="Website">
                  <Input
                    value={draft.website}
                    disabled={!canEdit}
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, website: e.target.value }))
                    }
                    onBlur={() => fieldsAutosave.flush()}
                  />
                </FieldRow>
                <div
                  className={`grid gap-3 ${contactType === "person" ? "grid-cols-1" : "grid-cols-2"}`}
                >
                  <FieldRow label="ABN">
                    <Input
                      value={draft.abn}
                      disabled={!canEdit}
                      onChange={(e) =>
                        setDraft((d) => ({
                          ...d,
                          abn: e.target.value.replace(/\s/g, ""),
                        }))
                      }
                      onBlur={() => fieldsAutosave.flush()}
                      className="font-mono"
                    />
                  </FieldRow>
                  {contactType !== "person" && (
                    <FieldRow label="ACN">
                      <Input
                        value={draft.acn}
                        disabled={!canEdit}
                        onChange={(e) =>
                          setDraft((d) => ({
                            ...d,
                            acn: e.target.value.replace(/\s/g, ""),
                          }))
                        }
                        onBlur={() => fieldsAutosave.flush()}
                        className="font-mono"
                      />
                    </FieldRow>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <h4 className="font-sans text-xs font-semibold uppercase tracking-wider text-[#121212]">
                    Addresses
                  </h4>
                  <div className="flex items-center gap-2">
                    {addressesDraft.length > 0 && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 gap-1.5 text-xs"
                        disabled={isArchived || !canEdit}
                        type="button"
                        onClick={() => {
                          const next = [
                            ...addressesDraftRef.current,
                            createEmptyAddress(),
                          ];
                          addressesDraftRef.current = next;
                          setAddressesDraft(next);
                          void addressesAutosave.flush();
                        }}
                      >
                        <Plus className="h-3.5 w-3.5" />
                        Add address
                      </Button>
                    )}
                    <AutosaveStatus
                      status={addressesAutosave.status}
                      lastSavedAt={addressesAutosave.lastSavedAt}
                      onRetry={addressesAutosave.retry}
                    />
                  </div>
                </div>
                <AddressesEditor
                  addresses={addressesDraft}
                  disabled={isArchived || !canEdit}
                  showTrailingAdd={false}
                  onChange={(next) => setAddressesDraft(next)}
                  onCommit={() => addressesAutosave.flush()}
                />
              </section>

              {firm.category === "referrer" && (
                <>
                  <Separator />
                  <ReferrerPerformanceSection firmContactId={firmContactId} />
                </>
              )}

              {(fieldDefinitions?.length ?? 0) > 0 && (
                <>
                  <Separator />
                  <section className="flex flex-col gap-3">
                    <div className="flex items-center justify-between">
                      <h3 className="font-sans text-xs font-semibold uppercase tracking-wider text-foreground">
                        Custom fields
                      </h3>
                      <AutosaveStatus
                        status={customFieldsAutosave.status}
                        lastSavedAt={customFieldsAutosave.lastSavedAt}
                        onRetry={customFieldsAutosave.retry}
                      />
                    </div>
                    {isOrgMember && (
                      <p className="rounded-md bg-muted/30 p-2 text-[11px] text-muted-foreground">
                        These fields are firm-entered (not from the user
                        profile) and pull through onto day-one documents.
                      </p>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                      {(fieldDefinitions ?? []).map((def) => (
                        <CustomFieldInput
                          key={def._id}
                          def={def}
                          value={customFieldsDraft[def.key] ?? ""}
                          disabled={isArchived || !canEdit}
                          onChange={(next) =>
                            setCustomFieldsDraft((d) => ({
                              ...d,
                              [def.key]: next,
                            }))
                          }
                          onCommit={() => customFieldsAutosave.flush()}
                        />
                      ))}
                    </div>
                  </section>
                </>
              )}

              {/* MOR-995 — people nested under this firm. Each row opens
                  that person's own card (with Back to return here). */}
              {contactType === "organisation" && nestedPeople.length > 0 && (
                <>
                  <Separator />
                  <section className="flex flex-col gap-3">
                    <h3 className="font-sans text-xs font-semibold uppercase tracking-wider text-foreground">
                      People at this firm ({nestedPeople.length})
                    </h3>
                    <ul className="flex flex-col gap-1">
                      {nestedPeople.map((c) => (
                        <li key={c._id}>
                          <button
                            type="button"
                            onClick={() => onOpenContact(c._id)}
                            className="flex w-full items-center justify-between gap-3 rounded-md border border-border bg-muted/10 px-3 py-2 text-left transition-colors hover:bg-muted/40"
                          >
                            <div className="min-w-0 flex-1">
                              <div className="truncate text-sm text-foreground">
                                {c.displayName}
                              </div>
                              {(c.email || c.phone) && (
                                <div className="truncate text-xs text-muted-foreground">
                                  {[c.email, c.phone]
                                    .filter(Boolean)
                                    .join(" · ")}
                                </div>
                              )}
                            </div>
                            <span className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-primary">
                              Open
                              <ArrowUpRight className="h-3 w-3" />
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </section>
                </>
              )}

              <Separator />

              {/* Jobs using this firm contact */}
              <section className="flex flex-col gap-3">
                <h3 className="font-sans text-xs font-semibold uppercase tracking-wider text-foreground">
                  Used on jobs ({jobsUsing?.length ?? 0})
                </h3>
                {!jobsUsing || jobsUsing.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    No active jobs reference this firm contact yet.
                  </p>
                ) : (
                  <ul className="flex flex-col gap-1">
                    {jobsUsing.map((j) => (
                      <li
                        key={j.jobContactId}
                        className="flex items-center justify-between gap-3 rounded-md border border-border bg-muted/10 px-3 py-2"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm text-foreground">
                            {j.jobName}
                          </div>
                          <div className="truncate text-xs text-muted-foreground">
                            as {j.contactDisplayName}
                          </div>
                        </div>
                        <Link
                          to="/dashboard/jobs/$jobId/contacts"
                          params={{ jobId: j.jobId }}
                          className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-primary hover:underline"
                        >
                          Open
                          <ArrowUpRight className="h-3 w-3" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              {!isArchived && canEdit && (
                <>
                  <Separator />
                  <section className="flex items-center justify-between gap-3">
                    <p className="text-xs text-muted-foreground">
                      Archive this contact when it&rsquo;s no longer in use.
                      Existing job links remain intact; new jobs won&rsquo;t
                      auto-link.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleArchive}
                      className="shrink-0 gap-2"
                    >
                      <Archive className="h-3.5 w-3.5" />
                      Archive
                    </Button>
                  </section>
                </>
              )}
            </div>
          </TabsContent>

          <TabsContent value="notes" className="mt-0 px-6 py-5">
            <FirmContactNotesFeed
              firmContactId={firmContactId}
              readOnly={isArchived}
              currentUserId={currentUserId}
              notesSummary={firm.notesSummary}
            />
            <p className="mt-3 text-[11px] text-muted-foreground">
              Firm-level notes are visible across every job that links to this
              contact. Use job-level notes for context that&rsquo;s specific to
              one appointment.
            </p>
          </TabsContent>

          <TabsContent value="history" className="mt-0 px-6 py-5">
            {firm.changeHistory.length === 0 ? (
              <div className="rounded-lg border border-dashed border-border bg-muted/10 p-6 text-center text-sm text-muted-foreground">
                No history yet.
              </div>
            ) : (
              <ChangeHistory
                history={firm.changeHistory}
                usersById={historyUsersById}
                formatters={CONTACT_FIELD_FORMATTERS}
                fieldLabels={CONTACT_FIELD_LABELS}
                title="Change history"
              />
            )}
          </TabsContent>
        </ScrollArea>
        <AiDisclaimer className="border-t border-border px-6 py-2" />
      </Tabs>
    </>
  );
}

function FieldRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-[11px] uppercase tracking-wider text-muted-foreground/80">
        {label}
      </Label>
      {children}
    </div>
  );
}

/**
 * Short appointment-type labels keyed by enum value. Reuses the shared
 * tag list from the precedent library, which mirrors `APPOINTMENT_TYPES`
 * in the schema (including the personal-insolvency types).
 */
const APPOINTMENT_TYPE_LABEL_BY_VALUE: Record<string, string> =
  Object.fromEntries(APPOINTMENT_TYPE_TAGS.map((t) => [t.value, t.label]));

/**
 * Referral performance for `referrer` contacts (MOR-882).
 *
 * Only mounted when the contact's category is `referrer`, so the stats
 * subscription never runs for other categories. `getReferrerStats` also
 * returns null server-side for non-referrer rows as a second guard.
 */
type ReferrerStats = NonNullable<
  FunctionReturnType<typeof api.preAppointment.pipeline.getReferrerStats>
>;

const OUTCOME_BUCKETS = [
  { key: "open", label: "Open" },
  { key: "converted", label: "Converted" },
  { key: "lost", label: "Lost" },
] as const;

/**
 * Custom pipeline field values across this referrer's jobs (MOR-948).
 *
 * Collapsed by default so the card stays readable: referral performance is
 * the headline, this is the drill-in. Numeric fields (currency, number)
 * total per outcome bucket; text and date fields can't be summed, so they
 * are counted as "set on N jobs" instead and only ever shown per job.
 *
 * Two switches keep the practitioner in control of the density: which
 * fields appear at all, and whether the section shows bucket totals or the
 * per-job breakdown.
 */
function ReferrerCustomFieldsSection({ stats }: { stats: ReferrerStats }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<"totals" | "byJob">("totals");
  const [hiddenFieldIds, setHiddenFieldIds] = useState<string[]>([]);

  // Only fields some referred job actually carries a value for — an unused
  // field would otherwise show a wall of zeroes on every referrer.
  const usedFields = useMemo(
    () =>
      stats.customFieldDefs.filter((field) =>
        stats.jobs.some((job) => job.customFields[field._id]),
      ),
    [stats],
  );
  const visibleFields = usedFields.filter(
    (f) => !hiddenFieldIds.includes(String(f._id)),
  );

  /** Totals (numeric) or set-counts (text/date) per outcome bucket. */
  const totals = useMemo(() => {
    return visibleFields.map((field) => {
      const buckets = OUTCOME_BUCKETS.map((bucket) => {
        const values = stats.jobs
          .filter((j) => j.outcome === bucket.key)
          .map((j) => j.customFields[field._id])
          .filter((v): v is string => Boolean(v));
        const sum = isSummable(field.type)
          ? values.reduce((acc, v) => acc + (Number(v) || 0), 0)
          : null;
        return { ...bucket, count: values.length, sum };
      });
      const allValues = stats.jobs
        .map((j) => j.customFields[field._id])
        .filter((v): v is string => Boolean(v));
      return {
        field,
        buckets,
        count: allValues.length,
        sum: isSummable(field.type)
          ? allValues.reduce((acc, v) => acc + (Number(v) || 0), 0)
          : null,
      };
    });
  }, [visibleFields, stats]);

  const jobsWithValues = useMemo(
    () =>
      stats.jobs.filter((job) =>
        visibleFields.some((f) => job.customFields[f._id]),
      ),
    [stats, visibleFields],
  );

  if (usedFields.length === 0) return null;

  return (
    <div className="flex flex-col gap-2 rounded-md border border-border">
      <button
        type="button"
        className="flex items-center justify-between gap-2 px-3 py-2 text-left"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="flex items-center gap-1.5 text-xs font-medium">
          {open ? (
            <ChevronDown className="size-3.5" />
          ) : (
            <ChevronRight className="size-3.5" />
          )}
          Custom fields
          <span className="text-muted-foreground">({usedFields.length})</span>
        </span>
        {!open && (
          <span className="flex flex-wrap justify-end gap-1">
            {totals
              .filter((t) => t.sum !== null)
              .slice(0, 2)
              .map((t) => (
                <CustomFieldBadge
                  key={String(t.field._id)}
                  field={{ ...t.field, _id: String(t.field._id) }}
                  value={String(t.sum)}
                  showName
                />
              ))}
          </span>
        )}
      </button>

      {open && (
        <div className="flex flex-col gap-3 border-t border-border px-3 py-2.5">
          {/* Density controls — what shows, and at what grain. */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex rounded-md border border-border p-0.5">
              {(["totals", "byJob"] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMode(m)}
                  className={cn(
                    "rounded px-2 py-0.5 text-xs transition",
                    mode === m
                      ? "bg-muted font-medium text-foreground"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {m === "totals" ? "Totals" : "By job"}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-1">
              {usedFields.map((field) => {
                const hidden = hiddenFieldIds.includes(String(field._id));
                return (
                  <button
                    key={String(field._id)}
                    type="button"
                    title={hidden ? `Show ${field.name}` : `Hide ${field.name}`}
                    onClick={() =>
                      setHiddenFieldIds((prev) =>
                        hidden
                          ? prev.filter((id) => id !== String(field._id))
                          : [...prev, String(field._id)],
                      )
                    }
                    className={cn(
                      "rounded border px-1.5 py-0.5 text-[10px] transition",
                      hidden
                        ? "text-muted-foreground/60 line-through"
                        : "border-primary/30 bg-primary/5 text-foreground",
                    )}
                  >
                    {field.name}
                  </button>
                );
              })}
            </div>
          </div>

          {visibleFields.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              All fields hidden — turn one back on above.
            </p>
          ) : mode === "totals" ? (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-muted-foreground">
                    <th className="pb-1 text-left font-medium">Field</th>
                    {OUTCOME_BUCKETS.map((b) => (
                      <th key={b.key} className="pb-1 text-right font-medium">
                        {b.label}
                      </th>
                    ))}
                    <th className="pb-1 text-right font-medium">All</th>
                  </tr>
                </thead>
                <tbody>
                  {totals.map((row) => (
                    <tr
                      key={String(row.field._id)}
                      className="border-t border-border/50"
                    >
                      <td className="py-1 pr-2">{row.field.name}</td>
                      {row.buckets.map((b) => (
                        <td
                          key={b.key}
                          className="py-1 text-right tabular-nums"
                        >
                          {b.sum !== null
                            ? b.count > 0
                              ? formatCustomFieldValue(
                                  row.field.type,
                                  String(b.sum),
                                )
                              : "—"
                            : b.count > 0
                              ? `${b.count} job${b.count === 1 ? "" : "s"}`
                              : "—"}
                        </td>
                      ))}
                      <td className="py-1 text-right font-medium tabular-nums">
                        {row.sum !== null
                          ? formatCustomFieldValue(
                              row.field.type,
                              String(row.sum),
                            )
                          : `${row.count} job${row.count === 1 ? "" : "s"}`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {totals.some((t) => t.sum === null) && (
                <p className="mt-1 text-[10px] text-muted-foreground">
                  Text and date fields can&rsquo;t be totalled — they show how
                  many jobs carry a value. Switch to By job to read them.
                </p>
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {OUTCOME_BUCKETS.map((bucket) => {
                const rows = jobsWithValues.filter(
                  (j) => j.outcome === bucket.key,
                );
                if (rows.length === 0) return null;
                return (
                  <div key={bucket.key} className="flex flex-col gap-1">
                    <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                      {bucket.label} ({rows.length})
                    </p>
                    {rows.map((job) => (
                      <div
                        key={String(job._id)}
                        className="flex items-start justify-between gap-2 rounded border border-border/60 px-2 py-1"
                      >
                        <span className="min-w-0 flex-1 truncate text-xs">
                          {job.entityName}
                        </span>
                        <span className="flex flex-wrap justify-end gap-1">
                          {visibleFields.map((field) => {
                            const value = job.customFields[field._id];
                            if (!value) return null;
                            return (
                              <CustomFieldBadge
                                key={String(field._id)}
                                field={{ ...field, _id: String(field._id) }}
                                value={value}
                              />
                            );
                          })}
                        </span>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ReferrerPerformanceSection({
  firmContactId,
}: {
  firmContactId: Id<"firmContacts">;
}) {
  const { data: stats, isLoading } = useQuery({
    ...convexQuery(api.preAppointment.pipeline.getReferrerStats, {
      firmContactId,
    }),
  });

  if (isLoading || stats === undefined) {
    return (
      <section className="flex flex-col gap-3">
        <h3 className="font-sans text-xs font-semibold uppercase tracking-wider text-foreground">
          Referral performance
        </h3>
        <p className="text-xs text-muted-foreground">Loading referral stats…</p>
      </section>
    );
  }

  // Null means the row isn't (or is no longer) a referrer server-side.
  if (stats === null) return null;

  const conversionPct =
    stats.conversionRatio === null
      ? null
      : Math.round(stats.conversionRatio * 100);

  return (
    <section className="flex flex-col gap-3">
      <h3 className="font-sans text-xs font-semibold uppercase tracking-wider text-foreground">
        Referral performance
      </h3>

      <div className="grid grid-cols-4 gap-2">
        <ReferrerStatTile label="Referred" value={stats.totalJobs} />
        <ReferrerStatTile label="Open" value={stats.openJobs} />
        <ReferrerStatTile label="Converted" value={stats.convertedJobs} />
        <ReferrerStatTile label="Lost" value={stats.lostJobs} />
      </div>

      <div className="flex items-baseline gap-2 rounded-md border border-border bg-muted/10 px-3 py-2">
        <span className="text-sm font-semibold tabular-nums text-foreground">
          {conversionPct === null ? "—" : `${conversionPct}%`}
        </span>
        <span className="text-xs text-muted-foreground">
          conversion rate — converted ÷ decided; open referrals aren&rsquo;t
          counted yet
        </span>
      </div>

      <ReferrerCustomFieldsSection stats={stats} />

      {stats.byAppointmentType.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {stats.byAppointmentType.map(({ appointmentType, count }) => (
            <Badge
              key={appointmentType}
              variant="outline"
              color="teal"
              size="sm"
            >
              {APPOINTMENT_TYPE_LABEL_BY_VALUE[appointmentType] ??
                appointmentType}
              <span className="tabular-nums opacity-70">×{count}</span>
            </Badge>
          ))}
        </div>
      )}

      {/* MOR-995 — when people nest under this firm, show who the
          referrals actually came from. Hidden for a single-source
          breakdown (a standalone contact's own referrals). */}
      {(stats.byContact?.length ?? 0) > 1 && (
        <div className="flex flex-col gap-1 rounded-md border border-border bg-muted/10 px-3 py-2">
          <span className="text-[11px] uppercase tracking-wider text-muted-foreground/80">
            By referrer
          </span>
          <ul className="flex flex-col gap-0.5">
            {(stats.byContact ?? []).map((c) => (
              <li
                key={c._id}
                className="flex items-baseline justify-between gap-3 text-xs"
              >
                <span className="truncate text-foreground">
                  {c.displayName}
                </span>
                <span className="tabular-nums text-muted-foreground">
                  {c.count}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {stats.jobs.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          No jobs have been referred by this contact yet.
        </p>
      ) : (
        <ul className="flex max-h-64 flex-col gap-1 overflow-y-auto pr-1">
          {stats.jobs.map((j) => (
            <li
              key={j._id}
              className="flex items-center justify-between gap-3 rounded-md border border-border bg-muted/10 px-3 py-2"
            >
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm text-foreground">
                  {j.entityName}
                </div>
                {j.appointmentType && (
                  <div className="truncate text-xs text-muted-foreground">
                    {APPOINTMENT_TYPE_LABEL_BY_VALUE[j.appointmentType] ??
                      j.appointmentType}
                  </div>
                )}
              </div>
              <Badge
                variant={JOB_STATUS_VARIANTS[j.status] ?? "outline"}
                size="sm"
                className="shrink-0"
              >
                {JOB_STATUS_LABELS[j.status] ?? j.status}
              </Badge>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function ReferrerStatTile({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex flex-col gap-0.5 rounded-md border border-border bg-muted/10 px-3 py-2">
      <span className="text-lg font-semibold tabular-nums leading-none text-foreground">
        {value}
      </span>
      <span className="text-[11px] uppercase tracking-wider text-muted-foreground/80">
        {label}
      </span>
    </div>
  );
}

type FirmContactFieldDefinition = {
  _id: Id<"firmContactFieldDefinitions">;
  category: FirmContactCategory;
  key: string;
  label: string;
  fieldType: "text" | "number" | "currency" | "date" | "select";
  selectOptions?: string[];
  placeholder?: string;
  sortOrder: number;
};

/**
 * Renders the right input widget for a single custom field definition.
 * Values are stored raw (e.g. `650` for a currency rate); formatting into
 * `$650.00` happens at render time when the value flows into a precedent.
 */
function CustomFieldInput({
  def,
  value,
  disabled,
  onChange,
  onCommit,
}: {
  def: FirmContactFieldDefinition;
  value: string;
  disabled: boolean;
  onChange: (next: string) => void;
  onCommit: () => void;
}) {
  if (def.fieldType === "select") {
    return (
      <FieldRow label={def.label}>
        <Select
          value={value || undefined}
          disabled={disabled}
          onValueChange={(v) => {
            onChange(v);
            setTimeout(onCommit, 0);
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder={def.placeholder ?? "Select…"} />
          </SelectTrigger>
          <SelectContent>
            {(def.selectOptions ?? []).map((opt) => (
              <SelectItem key={opt} value={opt}>
                {opt}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FieldRow>
    );
  }

  // Numeric/currency fields render as text inputs so users can type
  // formatted values like "30,000" — a native number input silently
  // rejects the comma keystrokes.
  const inputType = def.fieldType === "date" ? "date" : "text";

  return (
    <FieldRow label={def.label}>
      <div className="relative">
        {def.fieldType === "currency" && (
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
            $
          </span>
        )}
        <Input
          type={inputType}
          inputMode={
            def.fieldType === "currency" || def.fieldType === "number"
              ? "decimal"
              : undefined
          }
          value={value}
          disabled={disabled}
          placeholder={def.placeholder}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onCommit}
          className={def.fieldType === "currency" ? "pl-7" : undefined}
        />
      </div>
    </FieldRow>
  );
}
